/**
 * Handles tokenized unsubscribe links (?nl_unsub=<uuid>) on any page.
 * Works without sign-in: calls unsubscribe_by_token once, strips the param,
 * and shows a localized toast with an undo (re-subscribe) action.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { db } from '@/lib/supabaseQuery';
import { logger } from '@/utils/logger';
import { useLocalizations } from '@/hooks/useLocalizations';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const INITIAL_TOKEN: string | null = ((): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('nl_unsub');
})();

let consumed: string | null = null;

interface TokenResult {
  ok: boolean;
  reason?: string;
}

export function useNewsletterUnsubscribe(): void {
  const { lCommon } = useLocalizations();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    const live = new URL(window.location.href).searchParams.get('nl_unsub');
    const token = live || INITIAL_TOKEN;
    if (!token || token === consumed) return;
    ran.current = true;
    consumed = token;

    const clean = new URL(window.location.href);
    clean.searchParams.delete('nl_unsub');
    window.history.replaceState({}, '', clean.pathname + clean.search + clean.hash);

    const fail = (): void => {
      toast.error(lCommon('newsletter_unsubscribe.error', { defaultValue: 'This unsubscribe link is invalid.' }));
    };
    if (!UUID_RE.test(token)) { fail(); return; }

    const undo = async (): Promise<void> => {
      const { data, error } = await db.raw.rpc('resubscribe_by_token', { p_token: token });
      const res = data as unknown as TokenResult | null;
      if (error || !res?.ok) {
        logger.warn('[NewsletterUnsubscribe] undo failed', { error: error?.message, reason: res?.reason });
        toast.error(lCommon('newsletter_unsubscribe.undo_error', { defaultValue: 'We could not re-subscribe you. Please try again later.' }));
        return;
      }
      toast.success(lCommon('newsletter_unsubscribe.undo_success', { defaultValue: "You're subscribed again" }));
    };

    void (async (): Promise<void> => {
      const { data, error } = await db.raw.rpc('unsubscribe_by_token', { p_token: token, p_method: 'link' });
      const res = data as unknown as TokenResult | null;
      if (error || !res?.ok) {
        logger.warn('[NewsletterUnsubscribe] failed', { error: error?.message, reason: res?.reason });
        fail();
        return;
      }
      toast.success(lCommon('newsletter_unsubscribe.success', { defaultValue: "You've been unsubscribed. We won't email you again." }), {
        duration: 15000,
        action: {
          label: lCommon('newsletter_unsubscribe.undo', { defaultValue: 'I unsubscribed by mistake' }),
          onClick: () => { void undo(); },
        },
      });
    })();
  }, [lCommon]);
}
